import React, { useMemo, useState } from "react";
import { ENCLOSURE_LABELS, SPORT_LABELS } from "../../constants/courtDomain";
import { buildCourtRows, deriveMetrics } from "./courtStatsMetrics";
import { Meter, SortableHead } from "./StatsTable";
import { nextSort, sortRows } from "./statsSort";
import { fmtHours, fmtInt, fmtMoney, fmtPct, fmtPct0 } from "./statsFormat";

const COLUMNS = [
  { key: "name", label: "Cancha", numeric: false },
  { key: "reservations", label: "Reservas", numeric: true },
  { key: "bookedHours", label: "Horas reservadas", numeric: true },
  { key: "occupancy", label: "Ocupación", numeric: true },
  { key: "revenue", label: "Ingresos", numeric: true },
  { key: "revenuePerHour", label: "Ingreso por hora disp.", numeric: true },
  { key: "cancellationRate", label: "Cancelaciones", numeric: true },
  { key: "noShowRate", label: "Ausencias", numeric: true },
];

/**
 * Por cancha tab: one row per court with bookings, hours, occupancy (with a meter), revenue,
 * revenue per available hour and the cancellation / no-show rates. Every column sorts (the header
 * is a button and the `th` carries `aria-sort`); empty values always sort last. A total row closes
 * the table when more than one court is shown.
 *
 * @param {{current: object}} props  `current` is a stats response.
 */
export default function CanchasTab({ current }) {
  const [sort, setSort] = useState({ key: "revenuePerHour", dir: -1 });
  const rows = useMemo(() => sortRows(buildCourtRows(current.perCourt), sort.key, sort.dir), [current, sort]);
  const totals = deriveMetrics(current);

  return (
    <section className="stats-panel" aria-labelledby="stats-courts-title">
      <div className="stats-panel__head">
        <div>
          <h2 className="stats-panel__title" id="stats-courts-title">
            Rendimiento por cancha
          </h2>
          <p className="stats-panel__sub">Ordená por cualquier columna para encontrar la que más rinde.</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="stats-footnote">No hay canchas para mostrar con estos filtros.</p>
      ) : (
        <div className="stats-table-wrap">
          <table>
            <SortableHead columns={COLUMNS} sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} />
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className="stats-court-name">{row.name}</span>
                    <span className="stats-court-meta">
                      {[SPORT_LABELS[row.sport] || row.sport, ENCLOSURE_LABELS[row.enclosure]].filter(Boolean).join(" · ")}
                    </span>
                  </td>
                  <td className="num">{fmtInt(row.reservations)}</td>
                  <td className="num">{fmtHours(row.bookedHours)}</td>
                  <td className="num">
                    <Meter value={row.occupancy}>{fmtPct0(row.occupancy)}</Meter>
                  </td>
                  <td className="num">{fmtMoney(row.revenue)}</td>
                  <td className="num">{fmtMoney(row.revenuePerHour)}</td>
                  <td className="num">{fmtPct(row.cancellationRate)}</td>
                  <td className="num">{fmtPct(row.noShowRate)}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 1 && (
              <tfoot>
                <tr>
                  <td>Total del club</td>
                  <td className="num">{fmtInt(totals.reservations)}</td>
                  <td className="num">{fmtHours(totals.bookedHours)}</td>
                  <td className="num">{fmtPct0(totals.occupancy)}</td>
                  <td className="num">{fmtMoney(totals.revenue)}</td>
                  <td className="num">{fmtMoney(totals.revenuePerHour)}</td>
                  <td className="num">{fmtPct(totals.cancellationRate)}</td>
                  <td className="num">{fmtPct(totals.noShowRate)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </section>
  );
}
